"use client";

import React, { useMemo, FC } from "react";

import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import { clsx } from "clsx";
import NextLink from "next/link";
import { useParams } from "next/navigation";

import { GlobalPathParams } from "~/app/[locale]/types";
import { EditorialContentProps } from "~/bff/components/EditorialContent";
import { getLinkUrl } from "~/helpers/locales";
import { getTestAutomationProps } from "~/helpers/test-automation-props";
import { useLink } from "~/hooks/use-link/hook";
import { EvergageContentZones } from "~/lib/salesforce/evergage/types";

import { CTALinkButton } from "../cta-link-button/component";

import {
  EDITORIAL_CONTENT_CLASSES,
  MASONRY_COLUMNS,
  MOBILE_VISIBLE_ITEMS,
} from "./constants";
import { EditorialContentContainer } from "./styled";

export const testIds = {
  container: "editorial-content",
};

export const EditorialContent: FC<EditorialContentProps> = ({
  meta,
  link,
  title,
  subtitle,
  children,
  isFullViewOnMobile,
  testAutomationId = testIds.container,
}) => {
  const { locale } = useParams<GlobalPathParams>();
  const { shouldDisableLink } = useLink(
    getLinkUrl(locale, link?.url ?? undefined),
    locale,
  );
  const gridChildren = children ?? [];
  const isLimitedOnMobile =
    !isFullViewOnMobile && gridChildren.length > MOBILE_VISIBLE_ITEMS;

  const actionButton = useMemo(() => {
    return shouldDisableLink ? (
      <CTALinkButton
        className={EDITORIAL_CONTENT_CLASSES.ctaLinkButton}
        href={getLinkUrl(locale, link?.url ?? undefined)}
      >
        {link?.label}
      </CTALinkButton>
    ) : (
      <CTALinkButton
        component={NextLink}
        prefetch={false}
        href={getLinkUrl(locale, link?.url || "")}
        className={EDITORIAL_CONTENT_CLASSES.ctaLinkButton}
      >
        {link?.label}
      </CTALinkButton>
    );
  }, [shouldDisableLink, link?.label, link?.url, locale]);

  const titleId = `editorial-content-title${meta?.deliveryId ? `-${meta?.deliveryId}` : ""}`;

  const id = `${testAutomationId}${meta?.deliveryId ? `-${meta?.deliveryId}` : ""}`;

  const renderHeader = (isLabel: boolean) => (
    <Box className={EDITORIAL_CONTENT_CLASSES.header}>
      <Typography
        className={EDITORIAL_CONTENT_CLASSES.subtitle}
        {...getTestAutomationProps("subtitle")}
      >
        {subtitle}
      </Typography>
      <Typography
        id={isLabel ? titleId : undefined}
        variant="h2"
        className={EDITORIAL_CONTENT_CLASSES.title}
        {...getTestAutomationProps("title")}
      >
        {title}
      </Typography>
      <Box className={EDITORIAL_CONTENT_CLASSES.linkDesktop}>{actionButton}</Box>
    </Box>
  );

  const renderItem = (index: number) => {
    const isOdd = index % 2;

    return (
      <Box
        key={index}
        style={{ order: index }}
        {...getTestAutomationProps("grid-item")}
        className={clsx({
          [EDITORIAL_CONTENT_CLASSES.gridItem]: true,
          [EDITORIAL_CONTENT_CLASSES.gridItemLeft]: isOdd,
          [EDITORIAL_CONTENT_CLASSES.gridItemRight]: !isOdd,
          [EDITORIAL_CONTENT_CLASSES.gridItemLast]:
            index === gridChildren.length - 1,
          [EDITORIAL_CONTENT_CLASSES.gridItemLastOnMobile]:
            isLimitedOnMobile && index === MOBILE_VISIBLE_ITEMS - 1,
          [EDITORIAL_CONTENT_CLASSES.gridItemHiddenOnMobile]:
            isLimitedOnMobile && index >= MOBILE_VISIBLE_ITEMS,
        })}
      >
        {gridChildren[index] as React.ReactElement}
      </Box>
    );
  };

  const indexes = gridChildren.map((_, index) => index);
  const isSourceOrder = Boolean(isFullViewOnMobile);
  const rightColumn = indexes.filter((index) => index % MASONRY_COLUMNS === 0);
  const leftColumn = indexes.filter((index) => index % MASONRY_COLUMNS !== 0);

  return (
    <EditorialContentContainer
      {...getTestAutomationProps(testAutomationId)}
      id={id}
      data-evergage-zone={EvergageContentZones.EDITORIAL}
      role="group"
      aria-labelledby={titleId}
    >
      <Container className={EDITORIAL_CONTENT_CLASSES.grid}>
        <Box
          sx={{ display: { xs: "block", sm: "block", md: "none" } }}
          className={EDITORIAL_CONTENT_CLASSES.mobileHeaderBox}
        >
          {renderHeader(true)}
        </Box>
        <Box
          className={clsx(EDITORIAL_CONTENT_CLASSES.masonry, {
            [EDITORIAL_CONTENT_CLASSES.masonrySourceOrder]: isSourceOrder,
          })}
        >
          <Box className={EDITORIAL_CONTENT_CLASSES.masonryHeaderCell}>
            <Box
              className={clsx(
                EDITORIAL_CONTENT_CLASSES.gridItem,
                EDITORIAL_CONTENT_CLASSES.gridItemLeft,
              )}
              sx={{ display: { xs: "none", sm: "none", md: "block" } }}
            >
              {renderHeader(false)}
            </Box>
          </Box>
          {isSourceOrder && indexes.map((index) => renderItem(index))}
          {!isSourceOrder && !!gridChildren.length && (
            <>
              <Box
                className={clsx(
                  EDITORIAL_CONTENT_CLASSES.masonryColumn,
                  EDITORIAL_CONTENT_CLASSES.masonryColumnRight,
                )}
                {...getTestAutomationProps("grid-column")}
              >
                {rightColumn.map((index) => renderItem(index))}
              </Box>
              <Box
                className={clsx(
                  EDITORIAL_CONTENT_CLASSES.masonryColumn,
                  EDITORIAL_CONTENT_CLASSES.masonryColumnLeft,
                )}
                {...getTestAutomationProps("grid-column")}
              >
                {leftColumn.map((index) => renderItem(index))}
              </Box>
            </>
          )}
        </Box>
        <Box className={EDITORIAL_CONTENT_CLASSES.linkMobile}>{actionButton}</Box>
      </Container>
    </EditorialContentContainer>
  );
};
